import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle2, Loader2, Mail } from "lucide-react";
import ServiceDeskAPIService from "@sd/services/APIs";
import { getSdForgotPasswordResetUrl, sd } from "@sd/lib/sdPaths";
import {
  SdAuthShell,
  sdAuthInputClassName,
  sdAuthLabelClassName,
  sdAuthPrimaryButtonClassName,
} from "@sd/components/auth/SdAuthShell";
import { parseSdAuthApiError } from "@sd/components/auth/parseSdAuthApiError";

export default function SDForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setSent(false);
    setLoading(true);
    try {
      await ServiceDeskAPIService.auth.forgotPasswordRequest(
        {
          email: email.trim(),
          redirect_url: getSdForgotPasswordResetUrl(),
        },
        { skipErrorNotification: true }
      );
      setSent(true);
    } catch (err: unknown) {
      setError(parseSdAuthApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SdAuthShell>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900">Forgot Password</h2>
        <p className="mt-1 text-sm text-slate-500">
          Enter your email and we will send a reset link.
        </p>
      </div>

      {error ? (
        <div
          className="mb-5 flex gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3.5 text-sm text-red-800"
          role="alert"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden />
          <p className="leading-relaxed">{error}</p>
        </div>
      ) : null}

      {sent ? (
        <div
          className="mb-5 flex gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3.5 text-sm text-emerald-800"
          role="status"
        >
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
          <p className="leading-relaxed">
            If an account exists for this email, a reset link has been sent.
          </p>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="sd-forgot-email" className={sdAuthLabelClassName}>
            Email
          </label>
          <div className="relative">
            <Mail
              className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400"
              strokeWidth={1.75}
              aria-hidden
            />
            <input
              id="sd-forgot-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="you@example.com"
              className={sdAuthInputClassName}
            />
          </div>
        </div>

        <button type="submit" disabled={loading} className={sdAuthPrimaryButtonClassName}>
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Sending…
            </>
          ) : (
            "Send Reset Link"
          )}
        </button>
      </form>

      <p className="mt-5 text-center">
        <Link
          to={sd("login")}
          className="text-sm font-semibold text-slate-800 hover:text-blue-700"
        >
          Back to Sign In
        </Link>
      </p>
    </SdAuthShell>
  );
}
